import { registerRoute } from '../router';
import { apiFetch, ApiError } from '../api';
import { isTenantAdmin } from '../state';
import { escapeHtml } from '../format';
import { openModal } from '../components/modal';

interface RoleRecord {
  id: string;
  tenantId: string | null;
  name: string;
  isSystem: boolean;
  permissions: string[];
  createdAt: string;
}

interface PermissionGrant {
  value: string;
  label: string;
}

interface PermissionGroup {
  label: string;
  grants: PermissionGrant[];
}

// The permission model is a flat list of wildcard-matchable grant strings (see
// src/server/lib/permissions.ts's matchesPermission) — this is every grant the system roles
// already use, plus the roles:*/members:* pair this phase introduced, offered as checkboxes
// rather than free text so an admin can't typo a permission string into silently doing nothing.
const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    label: 'Everything',
    grants: [
      { value: '*', label: 'Full access to everything' },
      { value: '*:read', label: 'View everything (read-only)' },
    ],
  },
  { label: 'Campaigns', grants: [
    { value: 'campaigns:*', label: 'Full access' },
    { value: 'campaigns:read', label: 'View' },
    { value: 'campaigns:send', label: 'Send' },
  ] },
  { label: 'Contacts', grants: [
    { value: 'contacts:*', label: 'Full access' },
    { value: 'contacts:read', label: 'View' },
  ] },
  { label: 'Templates', grants: [
    { value: 'templates:*', label: 'Full access' },
    { value: 'templates:read', label: 'View' },
  ] },
  { label: 'Providers', grants: [{ value: 'providers:read', label: 'View' }] },
  { label: 'Webhooks', grants: [
    { value: 'webhooks:*', label: 'Full access' },
    { value: 'webhooks:read', label: 'View' },
  ] },
  { label: 'Users', grants: [{ value: 'users:read', label: 'View' }] },
  { label: 'Roles', grants: [
    { value: 'roles:*', label: 'Full access' },
    { value: 'roles:read', label: 'View' },
  ] },
  { label: 'Members', grants: [
    { value: 'members:*', label: 'Full access' },
    { value: 'members:read', label: 'View' },
  ] },
];

let roles: RoleRecord[] = [];

async function loadRoles(container: HTMLElement): Promise<void> {
  roles = await apiFetch<RoleRecord[]>('/api/roles');
  renderList(container);
}

function permissionBadges(permissions: string[]): string {
  return permissions.map((p) => `<span class="badge badge--muted">${escapeHtml(p)}</span>`).join(' ');
}

function renderList(container: HTMLElement): void {
  const canManage = isTenantAdmin();
  const listEl = container.querySelector('#roles-list')!;

  listEl.innerHTML = roles
    .map(
      (role) => `
    <div class="card" style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:12px;gap:16px;">
      <div>
        <div style="font-weight:600;display:flex;align-items:center;gap:8px;">
          ${escapeHtml(role.name)}
          ${role.isSystem ? '<span class="badge badge--muted">system</span>' : ''}
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;">${permissionBadges(role.permissions)}</div>
      </div>
      ${
        canManage && !role.isSystem
          ? `<div style="display:flex;gap:8px;flex-shrink:0;">
               <button class="btn btn--ghost" data-action="edit" data-id="${role.id}">Edit</button>
               <button class="btn btn--ghost" data-action="delete" data-id="${role.id}">Delete</button>
             </div>`
          : ''
      }
    </div>`,
    )
    .join('');

  if (!canManage) return;

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const role = roles.find((r) => r.id === btn.dataset.id);
      if (role) openRoleModal(container, role);
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this role? Members currently assigned to it must be reassigned first.')) return;
      try {
        await apiFetch(`/api/roles/${btn.dataset.id}`, { method: 'DELETE' });
        await loadRoles(container);
      } catch (err) {
        alert(err instanceof ApiError ? err.message : 'Something went wrong.');
      }
    });
  });
}

function openRoleModal(container: HTMLElement, existing?: RoleRecord): void {
  const isEdit = Boolean(existing);
  const checkedSet = new Set(existing?.permissions ?? []);

  const groupsHtml = PERMISSION_GROUPS.map(
    (group) => `
    <div style="margin-bottom:12px;">
      <div style="font-weight:600;font-size:0.85rem;margin-bottom:6px;">${escapeHtml(group.label)}</div>
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${group.grants
          .map(
            (g) => `
          <label style="display:flex;align-items:center;gap:8px;font-weight:400;">
            <input type="checkbox" value="${g.value}" data-permission-checkbox ${checkedSet.has(g.value) ? 'checked' : ''}>
            ${escapeHtml(g.label)} <code style="color:var(--clr-text-muted);font-size:0.8rem;">${g.value}</code>
          </label>`,
          )
          .join('')}
      </div>
    </div>`,
  ).join('');

  const { root, close } = openModal(
    isEdit ? 'Edit role' : 'New role',
    `
    <form id="role-form">
      <div class="field">
        <label for="role-name">Name</label>
        <input class="input" id="role-name" required value="${existing ? escapeHtml(existing.name) : ''}">
      </div>
      <div class="field">
        <label>Permissions</label>
        <div style="max-height:320px;overflow-y:auto;padding-right:4px;">${groupsHtml}</div>
      </div>
      <p class="error-text" id="role-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="role-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">${isEdit ? 'Save changes' : 'Create role'}</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#role-cancel')?.addEventListener('click', close);

  root.querySelector('#role-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#role-error')!;
    errorEl.textContent = '';

    const name = (root.querySelector('#role-name') as HTMLInputElement).value.trim();
    const permissions = Array.from(root.querySelectorAll<HTMLInputElement>('[data-permission-checkbox]:checked')).map((el) => el.value);

    if (permissions.length === 0) {
      errorEl.textContent = 'Select at least one permission.';
      return;
    }

    try {
      if (isEdit && existing) {
        await apiFetch(`/api/roles/${existing.id}`, { method: 'PATCH', body: JSON.stringify({ name, permissions }) });
      } else {
        await apiFetch('/api/roles', { method: 'POST', body: JSON.stringify({ name, permissions }) });
      }
      close();
      await loadRoles(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/roles',
  title: 'Roles',
  render: ({ container }) => {
    const canManage = isTenantAdmin();

    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div>
          <h1>Roles</h1>
          <p>System roles are available to every tenant and can't be edited. Custom roles are yours to define.</p>
        </div>
        ${canManage ? '<button class="btn btn--primary" id="new-role-btn">+ New role</button>' : ''}
      </div>
      <div id="roles-list">Loading…</div>
    `;

    container.querySelector('#new-role-btn')?.addEventListener('click', () => openRoleModal(container));

    loadRoles(container).catch(() => {
      container.querySelector('#roles-list')!.textContent = 'Could not load roles.';
    });
  },
});
